import { ColDef, ColGroupDef, IServerSideGetRowsRequest } from '@ag-grid-community/core';
import {
  cellStyleFunction,
  tooltipValueGetterFunction,
  valueGetterFunction
} from '../views/agGridReact/helpers/ColDefHelpers';
import { ValueRenderer } from '../views/agGridReact/ValueRenderer';
import { CustomColDefContext } from 'common/types/agGrid/context';
import { PIVOT_SEPARATOR } from '../views/agGridReact/Constants';

export function createPivotResultColumns(
  agRequest: IServerSideGetRowsRequest,
  pivotFields: string[],
  datasetUid: string
): (ColDef | ColGroupDef)[] {
  if (agRequest.pivotMode && agRequest.pivotCols.length > 0) {
    const pivotResultCols: ColGroupDef[] = [];
    pivotFields.forEach((field) =>
      addColDef(field, field.split(PIVOT_SEPARATOR), pivotResultCols, agRequest, datasetUid)
    );
    return pivotResultCols;
  }
  return [];
}

function addColDef(
  colId: string,
  parts: string[],
  pivotResultCols: (ColDef | ColGroupDef)[],
  agRequest: IServerSideGetRowsRequest,
  datasetUid: string
): (ColDef | ColGroupDef)[] {
  if (parts.length === 0) {
    return [];
  }

  const first = parts[0];
  const existingGroup = findExistingGroup(pivotResultCols, first);

  if (existingGroup) {
    existingGroup.children = addColDef(colId, parts.slice(1), existingGroup.children, agRequest, datasetUid);
  } else {
    const newColDef = createColDef(colId, parts, agRequest, datasetUid);
    const children = addColDef(colId, parts.slice(1), [], agRequest, datasetUid);

    if (children.length > 0) {
      newColDef.children = children;
    }

    pivotResultCols.push(newColDef);
  }

  return pivotResultCols;
}

function findExistingGroup(res: (ColDef | ColGroupDef)[], groupId: string): ColGroupDef | undefined {
  return res.find((r) => 'groupId' in r && r.groupId === groupId) as ColGroupDef;
}

function createColDef(
  colId: string,
  parts: string[],
  agRequest: IServerSideGetRowsRequest,
  datasetUid: string
): ColGroupDef {
  const isGroup = parts.length > 1;
  const colDef: any = {};

  if (isGroup) {
    colDef.groupId = parts[0];
    colDef.headerName = parts[0];
  } else {
    const valueCol = agRequest.valueCols.find((r) => r.field === parts[0]);
    if (valueCol) {
      colDef.colId = colId;
      colDef.headerName = valueCol.displayName;
      colDef.field = colId;
      colDef.context = {
        pivotValueColumn: valueCol.field,
        exporting: false,
        pivotAllowed: true
      } satisfies CustomColDefContext;
    }
  }

  colDef.cellRenderer = ValueRenderer;
  colDef.cellRendererParams = {
    datasetUid,
    wrapWithDivElement: true
  };
  colDef.cellStyle = cellStyleFunction;
  colDef.tooltipComponentParams = {
    datasetUid
  };
  colDef.tooltipValueGetter = tooltipValueGetterFunction;
  colDef.valueGetter = valueGetterFunction;
  colDef.suppressHeaderMenuButton = true;

  return colDef;
}
